import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Upload a file to Firebase Storage under tickets/{ticketId}/{random}/{filename}.
 * Returns the storage path (for the API metadata record) and a public-ish download URL.
 */
export async function uploadAttachmentFile(
  ticketId: number,
  file: File | Blob,
  filename: string,
  onProgress?: (pct: number) => void,
): Promise<{ storagePath: string; downloadURL: string }> {
  const uniqueId = crypto.randomUUID();
  // Encode filename to avoid breaking the path with weird characters.
  const safeName = filename.replace(/[^\w.\-]+/g, '_').slice(0, 200) || 'file';
  const storagePath = `tickets/${ticketId}/${uniqueId}/${safeName}`;
  const r = ref(storage, storagePath);

  const task = uploadBytesResumable(r, file, {
    contentType: file instanceof File ? file.type || 'application/octet-stream' : 'application/octet-stream',
  });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress) onProgress(snap.bytesTransferred / snap.totalBytes);
      },
      reject,
      () => resolve(),
    );
  });

  const downloadURL = await getDownloadURL(r);
  return { storagePath, downloadURL };
}

export async function getAttachmentDownloadURL(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath));
}

export async function deleteAttachmentFile(storagePath: string): Promise<void> {
  await deleteObject(ref(storage, storagePath));
}
